<div class="overflow-x-hidden bg-slate-50 font-inter text-slate-950">

  <!-- Hero -->
  <div class="border-b border-slate-100 bg-white px-4 py-20 sm:px-6 lg:px-8">
    <div class="mx-auto flex max-w-page flex-col gap-8">
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-4">
          <p class="text-body3 font-medium uppercase tracking-widest text-primary-600">Use Cases</p>
          <h1 class="max-w-3xl text-[3rem] font-normal leading-[1.05] tracking-[-0.04em] text-black sm:text-[4rem]">
            See FormOps in action<br>
            <span class="text-slate-400">for your team</span>
          </h1>
        </div>
        <p class="max-w-2xl text-[1.2rem] leading-8 text-slate-500">
          From HR and IT to Finance and Healthcare — see exactly how teams like yours
          use FormOps to replace scattered tools, remove manual steps, and move faster.
        </p>
      </div>
      <div class="flex flex-wrap gap-3">
        <a routerLink="/signup"
          class="inline-flex h-12 items-center rounded-full bg-primary-600 px-7 text-body2 font-semibold text-white transition hover:bg-primary-700">
          Start free
        </a>
        <a routerLink="/pricing"
          class="inline-flex h-12 items-center rounded-full border border-slate-200 bg-white px-7 text-body2 font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50">
          View pricing
        </a>
      </div>
    </div>
  </div>

  <!-- Industry Filter Tabs -->
  <div class="sticky top-[4.5rem] z-40 border-b border-slate-100 bg-white/95 px-4 backdrop-blur-sm sm:px-6 lg:px-8">
    <div class="mx-auto max-w-page">
      <div class="flex gap-1 overflow-x-auto py-3">
        @for (industry of industries; track industry.id) {
          <button
            type="button"
            (click)="setIndustry(industry.id)"
            [class.bg-slate-950]="activeIndustry() === industry.id"
            [class.text-white]="activeIndustry() === industry.id"
            [class.text-slate-600]="activeIndustry() !== industry.id"
            class="flex-shrink-0 cursor-pointer rounded-full px-5 py-2 text-body3 font-medium transition-all duration-150 hover:bg-slate-100">
            {{ industry.label }}
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Alternating use-case sections -->
  <div class="mx-auto flex max-w-page flex-col gap-10 px-4 py-16 sm:px-6 lg:px-8">
    @for (uc of getFilteredUseCases(); track uc.id; let i = $index) {
      <section class="overflow-hidden rounded-[2rem] bg-white p-6 sm:p-8 lg:p-10">
        <div
          class="flex flex-col gap-10 lg:flex-row lg:items-center"
          [ngClass]="{ 'lg:flex-row-reverse': i % 2 === 1 }">

          <!-- Visual panel -->
          <div
            class="relative flex min-h-[420px] w-full items-center justify-center overflow-hidden rounded-[1.5rem] p-6 sm:min-h-[480px] sm:p-8 lg:w-1/2">
            <div
              class="absolute inset-0 bg-cover bg-center"
              [style.background-image]="'url(' + uc.bgImage + ')'">
            </div>
            <div class="absolute inset-0 bg-white/35"></div>

            <div class="relative z-10 w-full max-w-sm">
              <!-- FORM MOCK -->
              @if (uc.mockType === 'form') {
                <div class="flex flex-col gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-950/10 backdrop-blur">
                  <div class="flex items-center justify-between">
                    <p class="text-body3 font-semibold text-slate-950">New Employee Form</p>
                    <span class="rounded-full bg-warning-100 px-2.5 py-1 text-caption text-warning-700">In progress</span>
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="flex flex-col gap-0.5 rounded-xl bg-slate-50 px-4 py-3">
                      <p class="text-caption text-slate-500">Full name</p>
                      <p class="text-body3 font-medium text-slate-900">Alex Thompson</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-slate-50 px-4 py-3">
                      <p class="text-caption text-slate-500">Start date</p>
                      <p class="text-body3 font-medium text-slate-900">Jun 2, 2026</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-slate-50 px-4 py-3">
                      <p class="text-caption text-slate-500">Department</p>
                      <p class="text-body3 font-medium text-slate-900">Product & Engineering</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-primary-50 px-4 py-3">
                      <p class="text-caption text-primary-600">Equipment request · pending</p>
                      <p class="text-body3 font-medium text-slate-900">MacBook Pro M4 + external monitor</p>
                    </div>
                  </div>
                </div>
              }

              <!-- APPROVAL MOCK -->
              @if (uc.mockType === 'approval') {
                <div class="flex flex-col gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-950/10 backdrop-blur">
                  <div class="flex items-center justify-between">
                    <p class="text-body3 font-semibold text-slate-950">Approval Pipeline</p>
                    <span class="rounded-full bg-info-100 px-2.5 py-1 text-caption text-info-700">2 of 3 done</span>
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-3 rounded-xl bg-success-50 px-4 py-3">
                      <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-500">
                        <svg class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p class="text-body3 font-medium text-slate-900">Manager sign-off</p>
                        <p class="text-caption text-slate-500">Sarah Chen · 10 min ago</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-3 rounded-xl bg-success-50 px-4 py-3">
                      <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-500">
                        <svg class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p class="text-body3 font-medium text-slate-900">Security review</p>
                        <p class="text-caption text-slate-500">IT Team · 5 min ago</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                      <div class="h-6 w-6 shrink-0 rounded-full border-2 border-slate-300 bg-white"></div>
                      <div>
                        <p class="text-body3 font-medium text-slate-400">Provisioning</p>
                        <p class="text-caption text-slate-400">Awaiting final step</p>
                      </div>
                    </div>
                  </div>
                </div>
              }

              <!-- QUEUE MOCK -->
              @if (uc.mockType === 'queue') {
                <div class="flex flex-col gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-950/10 backdrop-blur">
                  <div class="flex items-center justify-between">
                    <p class="text-body3 font-semibold text-slate-950">Submission Queue</p>
                    <span class="rounded-full bg-success-100 px-2.5 py-1 text-caption text-success-700">8 pending</span>
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <div>
                        <p class="text-body3 font-medium text-slate-900">Acme Corp Ltd</p>
                        <p class="text-caption text-slate-500">Received 2 hrs ago</p>
                      </div>
                      <span class="rounded-full bg-warning-100 px-2.5 py-1 text-caption text-warning-700">Review</span>
                    </div>
                    <div class="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <div>
                        <p class="text-body3 font-medium text-slate-900">Stellar Supplies</p>
                        <p class="text-caption text-slate-500">Received 4 hrs ago</p>
                      </div>
                      <span class="rounded-full bg-info-100 px-2.5 py-1 text-caption text-info-700">Docs needed</span>
                    </div>
                    <div class="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                      <div>
                        <p class="text-body3 font-medium text-slate-900">Nordic Print Co</p>
                        <p class="text-caption text-slate-500">Received 1 day ago</p>
                      </div>
                      <span class="rounded-full bg-success-100 px-2.5 py-1 text-caption text-success-700">Approved</span>
                    </div>
                  </div>
                </div>
              }

              <!-- ANALYTICS MOCK -->
              @if (uc.mockType === 'analytics') {
                <div class="flex flex-col gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-950/10 backdrop-blur">
                  <div class="flex items-center justify-between">
                    <p class="text-body3 font-semibold text-slate-950">Lead Pipeline Overview</p>
                    <span class="rounded-full bg-success-100 px-2.5 py-1 text-caption text-success-700">+14 today</span>
                  </div>
                  <div class="grid grid-cols-3 gap-2">
                    <div class="flex flex-col gap-0.5 rounded-xl bg-primary-50 p-3 text-center">
                      <p class="text-caption text-slate-500">New</p>
                      <p class="text-body1 font-bold text-primary-700">47</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-warning-50 p-3 text-center">
                      <p class="text-caption text-slate-500">Qualified</p>
                      <p class="text-body1 font-bold text-warning-700">23</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-success-50 p-3 text-center">
                      <p class="text-caption text-slate-500">Converted</p>
                      <p class="text-body1 font-bold text-success-700">11</p>
                    </div>
                  </div>
                  <div class="flex h-10 items-end gap-1">
                    <div class="h-4 flex-1 rounded-t bg-primary-200"></div>
                    <div class="h-7 flex-1 rounded-t bg-primary-300"></div>
                    <div class="h-5 flex-1 rounded-t bg-primary-200"></div>
                    <div class="h-9 flex-1 rounded-t bg-primary-400"></div>
                    <div class="h-10 flex-1 rounded-t bg-primary-500"></div>
                    <div class="h-6 flex-1 rounded-t bg-primary-300"></div>
                    <div class="h-8 flex-1 rounded-t bg-primary-400"></div>
                  </div>
                </div>
              }

              <!-- CHECKLIST MOCK -->
              @if (uc.mockType === 'checklist') {
                <div class="flex flex-col gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-950/10 backdrop-blur">
                  <div class="flex items-center justify-between">
                    <p class="text-body3 font-semibold text-slate-950">Intake Checklist</p>
                    <span class="rounded-full bg-info-100 px-2.5 py-1 text-caption text-info-700">3 / 4 done</span>
                  </div>
                  <div class="flex flex-col gap-2">
                    @for (step of uc.steps; track step; let si = $index) {
                      <div class="flex items-center gap-3 rounded-xl px-4 py-3"
                        [class.bg-success-50]="si < 3"
                        [class.bg-slate-50]="si >= 3">
                        <div class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                          [class.bg-success-500]="si < 3"
                          [class.border-2]="si >= 3"
                          [class.border-slate-300]="si >= 3">
                          @if (si < 3) {
                            <svg class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                            </svg>
                          }
                        </div>
                        <p class="text-body3 font-medium"
                          [class.text-slate-900]="si < 3"
                          [class.text-slate-400]="si >= 3">
                          {{ step }}
                        </p>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- SURVEY MOCK -->
              @if (uc.mockType === 'survey') {
                <div class="flex flex-col gap-3 rounded-2xl border border-white/70 bg-white/90 p-4 shadow-xl shadow-slate-950/10 backdrop-blur">
                  <div class="flex items-center justify-between">
                    <p class="text-body3 font-semibold text-slate-950">AI Insight Summary</p>
                    <span class="rounded-full bg-secondary-100 px-2.5 py-1 text-caption text-secondary-700">Live</span>
                  </div>
                  <div class="flex flex-col gap-1 rounded-xl bg-secondary-50 px-4 py-3">
                    <p class="text-caption text-secondary-600">Top signal this week</p>
                    <p class="text-body3 font-medium leading-6 text-slate-900">
                      "Work-life balance" mentioned 41% more — likely driven by recent policy change.
                    </p>
                  </div>
                  <div class="grid grid-cols-3 gap-2">
                    <div class="flex flex-col gap-0.5 rounded-xl bg-success-50 p-3 text-center">
                      <p class="text-caption text-slate-500">Positive</p>
                      <p class="text-body2 font-bold text-success-700">68%</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-info-50 p-3 text-center">
                      <p class="text-caption text-slate-500">Neutral</p>
                      <p class="text-body2 font-bold text-info-700">22%</p>
                    </div>
                    <div class="flex flex-col gap-0.5 rounded-xl bg-error-50 p-3 text-center">
                      <p class="text-caption text-slate-500">Concerns</p>
                      <p class="text-body2 font-bold text-error-700">10%</p>
                    </div>
                  </div>
                  <div class="flex h-8 items-end gap-1">
                    <div class="h-4 flex-1 rounded-t bg-secondary-200"></div>
                    <div class="h-6 flex-1 rounded-t bg-secondary-300"></div>
                    <div class="h-5 flex-1 rounded-t bg-secondary-200"></div>
                    <div class="h-8 flex-1 rounded-t bg-secondary-400"></div>
                    <div class="h-7 flex-1 rounded-t bg-secondary-500"></div>
                    <div class="h-6 flex-1 rounded-t bg-secondary-300"></div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Copy block -->
          <div class="flex w-full flex-col gap-6 lg:w-1/2">
            <span class="inline-flex w-fit rounded-full px-3 py-1 text-caption font-semibold" [ngClass]="uc.badgeColor">
              {{ uc.badgeText }}
            </span>

            <div class="flex flex-col gap-4">
              <h2 class="text-h3 font-semibold tracking-tight text-slate-950">{{ uc.title }}</h2>
              <p class="text-body1 leading-8 text-slate-600">{{ uc.description }}</p>
            </div>

            <div class="flex flex-wrap gap-2.5">
              @for (step of uc.steps; track step) {
                <span class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-2 text-body3 font-medium text-slate-700">
                  <svg class="h-3.5 w-3.5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                  {{ step }}
                </span>
              }
            </div>

            <a routerLink="/signup"
              class="inline-flex w-fit items-center gap-1.5 text-body2 font-semibold text-primary-600 transition hover:text-primary-700">
              Try this use case
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    }
  </div>

  <!-- CTA Banner -->
  <div class="mx-4 my-16 flex flex-col gap-8 overflow-hidden rounded-[2rem] bg-slate-950 px-8 py-16 text-center text-white sm:mx-6 lg:mx-8">
    <div class="flex flex-col gap-4">
      <p class="text-caption font-semibold uppercase tracking-widest text-slate-400">Ready to start?</p>
      <h2 class="text-h3 font-normal text-white">Your use case, built in minutes.</h2>
      <p class="mx-auto max-w-xl text-body1 text-slate-400">
        Pick a template, customise the fields, set up your workflow, and publish — no engineering required.
      </p>
    </div>
    <div class="flex flex-wrap justify-center gap-3">
      <a routerLink="/signup"
        class="inline-flex h-12 items-center rounded-full bg-white px-8 text-body2 font-semibold text-slate-950 transition hover:bg-slate-100">
        Start for free
      </a>
      <a routerLink="/pricing"
        class="inline-flex h-12 items-center rounded-full border border-slate-700 px-8 text-body2 font-medium text-slate-300 transition hover:border-slate-500 hover:text-white">
        See pricing
      </a>
    </div>
  </div>

</div>
